// Possible timestamp formats from Firebase
type FirebaseTimestamp =
  | { seconds: number; nanoseconds?: number } // Standard Firestore Timestamp
  | { _seconds: number; _nanoseconds?: number } // Cloud Functions Timestamp
  | { toDate: () => Date } // Native Firestore Timestamp with toDate method
  | Date // Already converted Date object
  | string // Date string
  | number // Unix timestamp
  | null
  | undefined;

export type DataType = {
  availability?: FirebaseTimestamp;
  availabilityTo?: FirebaseTimestamp;
  offerValidTo?: FirebaseTimestamp;
  createdAt?: FirebaseTimestamp;
  lastModified?: FirebaseTimestamp;
};

export type ReturnDataType = {
  availability: Date | null;
  availabilityTo: Date | null;
  offerValidTo: Date | null;
  createdAt: Date | null;
  lastModified: Date | null;
};

/**
 * Safely converts various Firebase timestamp formats to Date object
 */
export const convertToDate = (timestamp: FirebaseTimestamp): Date | null => {
  if (!timestamp) {
    return null;
  }

  try {
    // Already a Date object
    if (timestamp instanceof Date) {
      return isNaN(timestamp.getTime()) ? null : timestamp;
    }

    // Native Firestore Timestamp with toDate method
    if (typeof timestamp === 'object' && 'toDate' in timestamp && typeof timestamp.toDate === 'function') {
      const date = timestamp.toDate();
      return isNaN(date.getTime()) ? null : date;
    }

    // Standard Firestore Timestamp format { seconds, nanoseconds }
    if (typeof timestamp === 'object' && 'seconds' in timestamp && typeof timestamp.seconds === 'number') {
      const date = new Date(timestamp.seconds * 1000);
      return isNaN(date.getTime()) ? null : date;
    }

    // Cloud Functions Timestamp format { _seconds, _nanoseconds }
    if (typeof timestamp === 'object' && '_seconds' in timestamp && typeof timestamp._seconds === 'number') {
      const date = new Date(timestamp._seconds * 1000);
      return isNaN(date.getTime()) ? null : date;
    }

    // Unix timestamp (number)
    if (typeof timestamp === 'number') {
      // If timestamp is in seconds (typical for Firebase), convert to milliseconds
      const date = timestamp < 10000000000 ? new Date(timestamp * 1000) : new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }

    // Date string
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }

    // Unknown format
    console.warn('Unknown timestamp format:', timestamp);
    return null;
  } catch (error) {
    console.error('Error converting timestamp to date:', error, timestamp);
    return null;
  }
};

export const useProcessDates = (data: DataType): ReturnDataType => {
  return {
    availability: convertToDate(data.availability),
    availabilityTo: convertToDate(data.availabilityTo),
    offerValidTo: convertToDate(data.offerValidTo),
    createdAt: convertToDate(data.createdAt),
    lastModified: convertToDate(data.lastModified),
  };
};
